import type { CustomerDto } from '@tempo-io/fim3-dto';
import { useFim3Api } from '@/features/idea-management/hooks/useFim3Api';
import type { PublishedContextValue } from '@/features/views/components/published/PublishedContext';
import { usePublishedContext } from '@/features/views/components/published/PublishedContext';
import { usePublishedSnapshotQuery } from '@/features/views/components/published/usePublishedSnapshotQuery';

function toCustomerDto(resolved: { id: number; name: string }): CustomerDto {
  return {
    id: resolved.id,
    name: resolved.name,
    accountId: '',
    logoKey: null,
    logoUrl: null,
    fields: [],
    createdAt: new Date(0),
    updatedAt: new Date(0),
  };
}

function resolvePublishedCustomers(
  ctx: PublishedContextValue,
  customerIds: number[],
): { data: CustomerDto[] } {
  const panelItemId = ctx.panelItemId;
  const panelData = panelItemId !== undefined ? ctx.getItemPanelData(panelItemId) : undefined;
  const resolvedCustomers = panelData?.resolvedCustomers ?? {};
  const data = customerIds
    .map((id): CustomerDto | undefined => {
      const resolved = resolvedCustomers[String(id)];
      if (!resolved) return undefined;
      return toCustomerDto(resolved);
    })
    .filter((c): c is CustomerDto => c !== undefined);
  return { data };
}

export const useCustomersByIdsQueryWithPublished = (customerIds: number[]) => {
  const publishedContext = usePublishedContext();
  const isPublished = publishedContext !== null;
  const { customersApi } = useFim3Api();

  const liveQuery = customersApi.useGetSome(isPublished ? [] : customerIds);

  const publishedQuery = usePublishedSnapshotQuery({
    baseKey: ['customersByIds', customerIds],
    getSnapshot: () =>
      publishedContext ? resolvePublishedCustomers(publishedContext, customerIds) : undefined,
  });

  return isPublished ? publishedQuery : liveQuery;
};
